import React, { useEffect, useState } from "react";
import {
  TextField,
  Box,
  Grid,
  Typography,
  TextareaAutosize,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
} from "@mui/material";
import dataService from "../../services/dataService";
import { VirtualProductQuoteRequest } from "../../models/virtualProduct/types";
import headerImg from "../../../img/renaissance_header.jpg";
import { BranchAutocomplete } from "./branchAutocomplete";
import CommonHelper from "@/app/helpers/CommonHelper";

export interface RenaissanceProductModel {
  name: string;
  companyName: string;
  phoneNumber: string;
  emailAddress: string;
  projectAddress: string;
  jobDescription: string;
  branch: string;
  preferredContact: "Phone" | "Email";
  comments: string;
}

const initialModel: RenaissanceProductModel = {
  name: "",
  companyName: "",
  phoneNumber: "",
  emailAddress: "",
  projectAddress: "",
  jobDescription: "",
  branch: "",
  preferredContact: "Phone",
  comments: "",
};

const RenaissanceProduct: React.FC = () => {
  const [model, setModel] = useState<RenaissanceProductModel>(initialModel);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [productId] = useState(() => {
    return Number(
      new URLSearchParams(window.location.search).get("productId") || 0
    );
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadQuoteData = async () => {
      try {
        const response = await dataService.getRenaissanceQuoteRequest(
          productId
        );

        if (response.data.success && response.data.data) {
          const data = response.data.data;

          setModel((prev) => ({
            ...prev,
            name: data.name ?? "",
            companyName: data.companyName ?? "",
            branch: data.branch ?? "",
            phoneNumber: data.phoneNumber
              ? CommonHelper.formatPhone334(data.phoneNumber)
              : "",
            emailAddress: data.emailAddress ?? "",
          }));
        }
      } catch (error) {
        console.error("Failed to load Renaissance quote data:", error);
      }
    };

    loadQuoteData();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    let newValue = value;

    if (name === "phoneNumber") {
      newValue = CommonHelper.formatPhone334(value);
    }

    setModel((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    setErrors((prevErrors) => {
      const updated = { ...prevErrors };
      delete updated[name];
      return updated;
    });
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModel((prev) => ({
      ...prev,
      preferredContact: e.target.value as "Phone" | "Email",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    try {
      const payload: VirtualProductQuoteRequest = {
        CompanyName: model.companyName,
        Branch: model.branch,
        PreferredMethodofContact: model.preferredContact,
        PhoneNumber: CommonHelper.formatPhone334(model.phoneNumber),
        EmailAddress: model.emailAddress,
        JobLocation: model.projectAddress,
        ProjectAddress: model.projectAddress,
        JobDescription: model.jobDescription,
        SpecialRequirements: model.comments,
        ContactName: model.name,
        JobName: model.name,
        IsNavigatedFromProduct: true,
      };

      const response = await dataService.sendRenaissanceQuoteRequest(
        productId,
        payload
      );

      if (response.data.success) {
        setSubmitSuccess(true);
        setSubmitMessage(
          response.data.message ?? "Your quote request has been submitted."
        );
      } else {
        setSubmitSuccess(false);
        setSubmitMessage(response.data.message ?? "Submission failed.");
      }
    } catch (error) {
      console.error("Submission failed:", error);
      setSubmitSuccess(false);
      setSubmitMessage("Unexpected server error occurred.");
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const rawPhone = CommonHelper.normalizedPhone(model.phoneNumber);

    if (!model.name.trim()) errors.name = "Required**";
    if (!model.companyName.trim()) errors.companyName = "Required**";
    if (!rawPhone) {
      errors.phoneNumber = "Required**";
    } else if (rawPhone.length !== 10) {
      errors.phoneNumber = "Please enter a valid phone number.";
    }
    if (!model.emailAddress.trim()) {
      errors.emailAddress = "Required**";
    } else if (!emailRegex.test(model.emailAddress)) {
      errors.emailAddress = "Please enter a valid email address.";
    }

    return errors;
  };

  return (
    <>
      {!submitSuccess ? (
        <Box>
          <Box
            sx={{
              position: "relative",
              display: "inline-block",
              margin: "10px 50px",
            }}
          >
            <img
              src={headerImg}
              alt="Renaissance Patio Products"
              style={{ maxWidth: "100%", display: "block" }}
            />
            <div className="vp-triangle"></div>
          </Box>

          <Box textAlign="center" my={4} px={2}>
            <Typography variant="body1" maxWidth="md" mx="auto">
              Manufactured and supported locally, Renaissance Patio products are
              easy to design, easy to order, fast to build, and highly
              profitable. The Renaissance system lets you build the complete
              package for your customer, and make more money doing it.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" mt={3}>
              Complete the form below and one of our experts will reach out to
              you about your project.
            </Typography>
          </Box>
          <Box className="vp-container">
            <Grid
              container
              columnSpacing={4}
              rowSpacing={2}
              alignItems="start"
              sx={{
                backgroundColor: "#e2e2e2",
                borderRadius: 2,
                px: 3,
                py: 4,
              }}
            >
              <Grid item xs={12} sm={6}>
                <div className="form-group">
                  <label className="form-label" htmlFor="name">
                    Name
                  </label>
                  <TextField
                    id="name"
                    name="name"
                    value={model.name}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    className="form-input"
                    size="small"
                    required
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="companyName">
                    Company Name
                  </label>
                  <TextField
                    id="companyName"
                    name="companyName"
                    value={model.companyName}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    className="form-input"
                    size="small"
                    required
                    error={!!errors.companyName}
                    helperText={errors.companyName}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="phoneNumber">
                    Phone Number
                  </label>
                  <TextField
                    id="phoneNumber"
                    name="phoneNumber"
                    value={CommonHelper.formatPhone334(model.phoneNumber)}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    className="form-input"
                    size="small"
                    required
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="emailAddress">
                    Email Address
                  </label>
                  <TextField
                    id="emailAddress"
                    name="emailAddress"
                    value={model.emailAddress}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    className="form-input"
                    size="small"
                    required
                    error={!!errors.emailAddress}
                    helperText={errors.emailAddress}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="projectAddress">
                    Project Address
                  </label>
                  <TextField
                    id="projectAddress"
                    name="projectAddress"
                    value={model.projectAddress}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    className="form-input"
                    size="small"
                  />
                </div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <div className="form-group">
                  <label className="form-label" htmlFor="jobDescription">
                    Job Description
                  </label>
                  <TextField
                    id="jobDescription"
                    name="jobDescription"
                    value={model.jobDescription}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    className="form-input"
                    size="small"
                    multiline
                    minRows={3}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Branch</label>
                  <BranchAutocomplete
                    branchInitialValue={{ id: "0", displayName: model.branch, value: "" }}
                    onChange={(val) => {
                      model.branch = val?.displayName ?? "";
                    }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Preferred Method of Contact
                  </label>
                  <RadioGroup
                    row
                    name="preferredContact"
                    value={model.preferredContact}
                    onChange={handleRadioChange}
                  >
                    <FormControlLabel
                      value="Phone"
                      control={<Radio />}
                      label="Phone"
                    />
                    <FormControlLabel
                      value="Email"
                      control={<Radio />}
                      label="Email"
                    />
                  </RadioGroup>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Special Requirements/Comments
                  </label>
                  <TextareaAutosize
                    minRows={4}
                    name="comments"
                    className="textarea-field"
                    placeholder="Comments"
                    value={model.comments}
                    onChange={handleChange}
                    style={{
                      width: "100%",
                      borderRadius: "4px",
                      padding: "8px",
                      borderColor: "#ccc",
                      resize: "vertical",
                    }}
                  />
                </div>
              </Grid>
            </Grid>

            <Button
              onClick={handleSubmit}
              className="blueButtonOutline submit-button-blue"
              sx={{ mt: 3 }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              position: "relative",
              display: "inline-block",
              margin: "10px 50px",
            }}
          >
            <img
              src={headerImg}
              alt="Renaissance Patio Products"
              style={{ maxWidth: "100%", display: "block" }}
            />
            <div className="vp-triangle"></div>
          </Box>
          <Box
            className="vp-container"
            sx={{
              textAlign: "center",
              py: 10,
              maxWidth: "900px",
              margin: "auto",
            }}
          >
            <Typography variant="body1" maxWidth="md" mx="auto" mb={4}>
              Manufactured and supported locally, Renaissance Patio products are
              easy to design, easy to order, fast to build, and highly
              profitable. The Renaissance system lets you build the complete
              package for your customer, and make more money doing it.
            </Typography>

            <Box sx={{ lineHeight: "20px" }}>
              <Typography
                component="strong"
                fontWeight={600}
                mb={3}
                display="block"
              >
                Your quote request details have been sent to our Renaissance
                Team for review. They will be in touch as soon as possible with
                confirmation and next steps.
              </Typography>

              <Box display="flex" justifyContent="center" gap={2}>
                <Button
                  className="blueButtonOutline submit-button-white"
                  sx={{ mt: 3 }}
                  onClick={() =>
                    (window.location.href =
                      "/lbpcustomer/gallery?searchRenaissance=true")
                  }
                >
                  Renaissance Product
                </Button>
                <Button
                  className="blueButtonOutline submit-button-blue"
                  sx={{ mt: 3 }}
                  onClick={() => (window.location.href = "/lbpcustomer")}
                >
                  Return to LansingNow
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default RenaissanceProduct;
